import { useFlag } from '@reflag/react-sdk';
import clsx from 'clsx';
import * as React from 'react';

import { GitBookUsers } from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import {
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    isDocumentEmpty,
    MARK_BOLD,
    MARK_CODE,
    MARK_ITALIC,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
} from '@gitbook/doc-core';
import { Block, type Change, Inline, Mark, TextRange, type Value } from '@gitbook/slate';
import {
    Editor,
    type MarkProps,
    type NodeProps,
    type Plugin,
    useIsEditorComposing,
} from '@gitbook/slate-react';
import { Code } from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';
import { useEventCallback, useLiveRef, usePrevious } from '@gitbook/util-react-hooks';
import { isHttpUrl } from '@gitbook/util-url';

import { setKeyboardShortcutsScope } from '../../Keyboard';
import {
    createMentionPlugin,
    emojiPlugin,
    listPlugin,
    markdownBasicPlugins,
} from '../ContentEditor';
import { EditLinkPopoverProvider } from '../Link';
import { GitBookAgentAvatar } from '../Users';

const logger = Logger('app:basic-editor');

export interface BasicEditorRef {
    focus: (props?: { toEnd?: boolean }) => void;
}

export interface BasicEditorProps {
    /**
     * Value to dislay in the editor.
     */
    value: Value;
    /**
     * Callback when a change is made.
     */
    onChange: (change: Change) => void;
    /**
     * Callback when the editor is blurred.
     */
    onBlur?: () => void;
    /**
     * Callback when the user is submitting the content by pressing enter.
     * If none is passed, pressing enter will insert a new line.
     */
    onSubmit?: () => void;
    /**
     * Callback when the editor is focused.
     */
    onFocus?: () => void;
    /**
     * If true, the user can submit the content by pressing enter with the meta key.
     */
    submitWithMetaKey?: boolean;
    /**
     * If true, the editor will be focused when mounted.
     */
    autoFocus?: boolean;
    /**
     * Placeholder to display when the content is empty.
     */
    placeholder?: string;
    /**
     * Should the content be editable or not.
     */
    readOnly?: boolean;
    /**
     * Configuration for the type of mentions that can be inserted.
     */
    mentions?: Record<string, ContentRefKind[]>;
    /**
     * Accept mentioning the GitBook agent.
     */
    acceptAgentMention?: boolean;
    /**
     * Class name to apply to the editor.
     */
    className?: string;
    /**
     * Class name to use for typography.
     */
    typographyClassName?: string;
}

const defaultMentions: Record<string, ContentRefKind[]> = {
    '#': ['page', 'space', 'user'],
    '@': ['user', 'page', 'space'],
};

/**
 * Basic editor component, used for comments and prompts.
 */
export const BasicEditor = React.forwardRef(function BasicEditorWithRef(
    props: BasicEditorProps,
    ref: React.Ref<BasicEditorRef>
) {
    const {
        value,
        placeholder,
        autoFocus = false,
        readOnly = false,
        mentions = defaultMentions,
        acceptAgentMention = false,
        submitWithMetaKey = false,
        onSubmit,
        className,
        typographyClassName = 'text-ui-action',
    } = props;

    const docsAgentFeature = useFlag('DOCS_AGENTS');
    const onBlur = useEventCallback(props.onBlur ?? (() => {}));
    const onFocus = useEventCallback(props.onFocus ?? (() => {}));
    const onChange = useEventCallback(props.onChange);
    const mentionsRef = React.useRef(mentions);

    const submitPlugin = useOnSubmitPlugin(onSubmit, submitWithMetaKey);

    const plugins = React.useMemo(() => {
        return [
            { renderNode, renderMark },
            emojiPlugin,
            createMentionPlugin({
                accept: mentionsRef.current,
                extraGroupsAtStart:
                    acceptAgentMention && docsAgentFeature.isEnabled
                        ? [
                              (pick) => ({
                                  key: 'agents',
                                  label: 'Agents',
                                  commands: [
                                      {
                                          key: 'agent',
                                          title: 'GitBook Agent',
                                          subtitle: 'Mention and get the agent to help you',
                                          icon: <GitBookAgentAvatar size="md" loading />,
                                          keywords: ['agent'],
                                          trigger: () => {
                                              pick({
                                                  kind: 'user',
                                                  user: GitBookUsers.GitbookAgent,
                                              });
                                          },
                                      },
                                  ],
                              }),
                          ]
                        : [],
            }),
            listPlugin,
            ...markdownBasicPlugins,
            highlightUrlsPlugin,

            // Submit plugin should be at the end to not impact other plugins (ex: list)
            submitPlugin,
        ];
    }, [submitPlugin, acceptAgentMention, docsAgentFeature.isEnabled]);

    React.useImperativeHandle<BasicEditorRef, BasicEditorRef>(
        ref,
        () => ({
            focus: (props?: { toEnd?: boolean }) => {
                try {
                    const change = value.change();

                    if (props?.toEnd) {
                        // Try to find the last block and position cursor at its end
                        const lastBlock = value.document.nodes.findLast((node) =>
                            Block.isBlock(node)
                        );
                        if (lastBlock) {
                            const lastText = lastBlock.getLastText();
                            if (lastText) {
                                onChange(change.collapseToEndOf(lastText).focus());
                                return;
                            }
                        }
                        // Fallback to end of document if we can't find specific nodes
                        onChange(change.collapseToEndOf(change.value.document).focus());
                    } else {
                        onChange(change.focus());
                    }
                } catch (_error) {
                    // If focusing fails, try with a fresh selection
                    try {
                        const firstBlock = value.document.nodes.first();
                        if (firstBlock && Block.isBlock(firstBlock)) {
                            const firstText = firstBlock.getFirstText();
                            if (firstText) {
                                onChange(value.change().collapseToStartOf(firstText).focus());
                                return;
                            }
                        }
                    } catch (_fallbackError) {
                        logger.error('Failed to focus editor', { _fallbackError, value });
                    }
                }
            },
        }),
        [value, onChange]
    );

    /**
     * Whenever the editor is focused, switch to the 'basic-editor' keyboard scope
     * (preventing any top-level shortcuts from conflicting with editing)
     */
    React.useEffect(() => {
        if (!value.isFocused) {
            return;
        }

        return setKeyboardShortcutsScope('basic-editor');
    }, [value.isFocused]);

    /**
     * When the editor is blurred/focused, we trigger the callbacks.
     */
    const { isBlurred, isFocused } = value;
    const wasBlurred = usePrevious(isBlurred);
    const wasFocused = usePrevious(isFocused);

    React.useEffect(() => {
        if (isBlurred === true && wasBlurred === false) {
            onBlur();
        }
    }, [wasBlurred, isBlurred, onBlur]);

    React.useEffect(() => {
        if (isFocused === true && wasFocused === false) {
            onFocus();
        }
    }, [wasFocused, isFocused, onFocus]);

    return (
        <EditLinkPopoverProvider>
            <div
                className={clsx(
                    'relative flex flex-1 flex-col overflow-y-auto',
                    typographyClassName,
                    className
                )}
            >
                <Editor
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                    autoFocus={autoFocus}
                    plugins={plugins}
                    className="flex flex-col gap-3"
                >
                    {placeholder ? (
                        <Placeholder
                            value={value}
                            placeholder={placeholder}
                            className={className}
                        />
                    ) : null}
                </Editor>
            </div>
        </EditLinkPopoverProvider>
    );
});

function Placeholder(props: { value: Value; placeholder: string; className?: string }) {
    const { value, placeholder, className } = props;
    const isComposing = useIsEditorComposing();

    return isDocumentEmpty(value.document) && !isComposing ? (
        <div className={clsx('pointer-events-none absolute inset-0 text-muted', className)}>
            {placeholder}
        </div>
    ) : null;
}

function renderNode(props: NodeProps) {
    if (Block.isBlock(props.node) && !Inline.isInline(props.parent)) {
        switch (props.node.type) {
            case BLOCK_PARAGRAPH:
                return <p {...props.attributes}>{props.children}</p>;
            case BLOCK_LIST_UL:
                return (
                    <ul {...props.attributes} className="list-disc pl-5">
                        {props.children}
                    </ul>
                );
            case BLOCK_LIST_OL:
                return (
                    <ol {...props.attributes} className="list-decimal pl-5">
                        {props.children}
                    </ol>
                );
            case BLOCK_LIST_ITEM:
                return <li {...props.attributes}>{props.children}</li>;
        }
    }
}

function renderMark(props: MarkProps) {
    switch (props.mark.type) {
        case MARK_BOLD:
            return (
                <strong {...props.attributes} className="font-bold">
                    {props.children}
                </strong>
            );
        case MARK_ITALIC:
            return (
                <i {...props.attributes} className="italic">
                    {props.children}
                </i>
            );
        case MARK_CODE:
            return <Code {...props.attributes}>{props.children}</Code>;
        case MARK_STRIKETHROUGH:
            return (
                <del {...props.attributes} className="line-through">
                    {props.children}
                </del>
            );
        case MARK_SUPERSCRIPT:
            return <sup {...props.attributes}>{props.children}</sup>;
        case MARK_SUBSCRIPT:
            return <sub {...props.attributes}>{props.children}</sub>;
    }
}

/** Plugin to trigger onSubmit when pressing enter (without shift) */
export function useOnSubmitPlugin(onSubmit: (() => void) | undefined, useMetaKey = false): Plugin {
    const onSubmitRef = useLiveRef(onSubmit);
    const hasSubmit = Boolean(onSubmit);

    return React.useMemo(() => {
        if (!hasSubmit) {
            return {};
        }

        return {
            onKeyDown: (event) => {
                const onSubmitKeysPressed = useMetaKey
                    ? event.key === 'Enter' && event.metaKey
                    : event.key === 'Enter';

                if (onSubmitKeysPressed && !event.shiftKey) {
                    onSubmitRef.current?.();
                    return true;
                }
            },
        };
    }, [hasSubmit, onSubmitRef, useMetaKey]);
}

/** Plugin to highlight urls */
const highlightUrlsPlugin: Plugin = {
    decorateNode: (node) => {
        if (node.object !== 'block') {
            return;
        }

        const decorations: TextRange[] = [];

        node.getTexts().forEach((textNode) => {
            const { text } = textNode;
            const urlRegex = /https?:\/\/[^\s]+/g;
            let match: RegExpExecArray | null = null;

            while ((match = urlRegex.exec(text))) {
                const [url] = match;
                const { index } = match;

                if (!isHttpUrl(url)) {
                    continue;
                }

                decorations.push(
                    TextRange.create({
                        anchorKey: textNode.key,
                        anchorOffset: index,
                        focusKey: textNode.key,
                        focusOffset: index + url.length,
                        marks: [
                            Mark.create({
                                type: 'url',
                                data: {
                                    url,
                                },
                            }),
                        ],
                    })
                );
            }
        });

        return decorations;
    },
    renderMark: (props) => {
        const { mark, children, editor } = props;

        if (mark.type === 'url') {
            const url = props.mark.data.get('url');

            if (!editor.readOnly) {
                return (
                    <span {...props.attributes} className="text-primary underline">
                        {children}
                    </span>
                );
            }

            return (
                <a
                    {...props.attributes}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline"
                >
                    {children}
                </a>
            );
        }
    },
};
